import React, { useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './theme/theme';
import { LanguageProvider } from './i18n/LanguageContext';
import { ColorModeProvider, useColorMode } from './theme/ColorModeContext';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import MealsTab from './components/MealsTab';
import WeightTab from './components/WeightTab';
import ProfileSetup from './components/ProfileSetup';
import Billing from './components/Billing';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';
import AdminUsers from './components/admin/AdminUsers';
import AdminUserDetail from './components/admin/AdminUserDetail';
import ErrorBoundary from './components/ErrorBoundary';

function AppWithTheme() {
  const { mode } = useColorMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename={import.meta.env.BASE_URL}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Authenticated app */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="meals" element={<MealsTab />} />
            <Route path="weight" element={<WeightTab />} />
            <Route path="billing" element={<Billing />} />
            <Route path="profile" element={<ProfileSetup />} />
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* Admin portal */}
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:email" element={<AdminUserDetail />} />
            <Route index element={<Navigate to="users" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ColorModeProvider>
          <AppWithTheme />
        </ColorModeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
